import { getAssetPriceInfoWithFallback } from '@funkit/connect-core'
import { useQuery } from '@tanstack/react-query'

import { useIsStablecoin } from '../hooks/useIsStablecoin'
import { useFunkitIdentity } from '../identity/IdentityContext'
import { logger } from '../logging/logger'

const SOURCE_TOKEN_PRICE_QUERY_KEY = 'sourceTokenUsdPrice'

/** Long enough that typing doesn't refetch, short enough to stay honest. */
const PRICE_STALE_TIME_MS = 60_000

/** What a dollar stablecoin is worth, without asking anyone. */
const STABLECOIN_USD_PRICE = 1

/**
 * Spot USD price of the source token, for the USD floor and ceiling the host
 * configures (`getMinWithdrawalUSD` / `getMaxWithdrawalUSD`).
 *
 * A dollar stablecoin is priced from its symbol and never fetched, as web's
 * `useAssetAddressPrice` does — it is the common withdrawal source, and the
 * amount screen was waiting on a round trip to be told a dollar is a dollar.
 *
 * `0` means "unknown", which is what the core validator reads as "no price" —
 * so a failed or in-flight lookup leaves the USD limits inert rather than
 * judging an amount against a made-up rate. Per-token limits and the balance
 * check are unaffected either way.
 *
 * Not every source has a price: Lighter's L2 assets are addressed by index, not
 * by an ERC-20 funkit's price API knows, so that lookup 404s and core's Relay
 * fallback is what prices them.
 */
export function useSourceTokenUsdPrice(
  sourceToken:
    | { chainId: string | number; address: string; symbol: string }
    | undefined,
  {
    enabled = true,
    refetchInterval = false,
  }: {
    enabled?: boolean
    refetchInterval?: number | false
  } = {},
) {
  const { apiKey } = useFunkitIdentity()
  const isStablecoin = useIsStablecoin()
  const chainId = sourceToken ? String(sourceToken.chainId) : undefined
  const isStable = !!sourceToken && isStablecoin(sourceToken.symbol)
  const unavailable = enabled && !!sourceToken && !isStable && !apiKey

  const query = useQuery({
    queryKey: [
      SOURCE_TOKEN_PRICE_QUERY_KEY,
      apiKey,
      chainId,
      sourceToken?.address,
    ],
    queryFn: async () => {
      if (!sourceToken || !chainId) {
        throw new Error('Missing price inputs')
      }
      const info = await getAssetPriceInfoWithFallback({
        chainId,
        assetTokenAddress: sourceToken.address as `0x${string}`,
        apiKey,
        logger,
      })
      const unitPrice = info.unitPrice
      if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
        // Reported here, not on read: a cached bad price would otherwise warn
        // on every render. A non-positive price would silently scale every USD
        // limit to nothing.
        logger.warn('connect-rn:withdrawal:unusable_source_price', {
          chainId: sourceToken.chainId,
          address: sourceToken.address,
          unitPrice,
        })
        return 0
      }
      return unitPrice
    },
    enabled: enabled && !!sourceToken && !isStable && !unavailable,
    staleTime: PRICE_STALE_TIME_MS,
    refetchInterval: isStable ? false : refetchInterval,
    retry: false,
  })

  return {
    refetch: query.refetch,
    data: isStable ? STABLECOIN_USD_PRICE : (query.data ?? 0),
    isUnavailable: unavailable,
    isPending: !isStable && !unavailable && query.isPending,
    isError: !isStable && (unavailable || query.isError),
    isFetching: !isStable && query.isFetching,
  }
}
